/**
 * Utility to convert audio blob to WAV format
 * WAV format is required for JotForm audio file uploads
 */

/**
 * Converts an AudioBuffer to a WAV Blob
 */
export const audioBufferToWav = (audioBuffer: AudioBuffer): Blob => {
    const numChannels = audioBuffer.numberOfChannels
    const sampleRate = audioBuffer.sampleRate
    const format = 1 // PCM
    const bitDepth = 16

    // Interleave channels
    let interleaved: Float32Array
    if (numChannels === 2) {
        const left = audioBuffer.getChannelData(0)
        const right = audioBuffer.getChannelData(1)
        interleaved = new Float32Array(left.length + right.length)
        for (let i = 0; i < left.length; i++) {
            interleaved[i * 2] = left[i]
            interleaved[i * 2 + 1] = right[i]
        }
    } else {
        interleaved = audioBuffer.getChannelData(0)
    }

    // Create WAV file
    const dataLength = interleaved.length * (bitDepth / 8)
    const buffer = new ArrayBuffer(44 + dataLength)
    const view = new DataView(buffer)

    // RIFF chunk descriptor
    writeString(view, 0, 'RIFF')
    view.setUint32(4, 36 + dataLength, true)
    writeString(view, 8, 'WAVE')

    // fmt sub-chunk
    writeString(view, 12, 'fmt ')
    view.setUint32(16, 16, true) // Subchunk1Size
    view.setUint16(20, format, true) // AudioFormat
    view.setUint16(22, numChannels, true) // NumChannels
    view.setUint32(24, sampleRate, true) // SampleRate
    view.setUint32(28, sampleRate * numChannels * (bitDepth / 8), true) // ByteRate
    view.setUint16(32, numChannels * (bitDepth / 8), true) // BlockAlign
    view.setUint16(34, bitDepth, true) // BitsPerSample

    // data sub-chunk
    writeString(view, 36, 'data')
    view.setUint32(40, dataLength, true)

    // Write PCM samples
    let offset = 44
    for (let i = 0; i < interleaved.length; i++) {
        const sample = Math.max(-1, Math.min(1, interleaved[i]))
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true)
        offset += 2
    }

    return new Blob([buffer], { type: 'audio/wav' })
}

/**
 * Helper function to write a string to DataView
 */
const writeString = (view: DataView, offset: number, string: string): void => {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i))
    }
}

/**
 * Converts a Blob (webm/mp3/etc) to WAV format
 * Uses Web Audio API to decode and re-encode
 */
export const convertBlobToWav = async (audioBlob: Blob): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const audioContext = new AudioContext()
        const fileReader = new FileReader()

        fileReader.onload = async (event) => {
            try {
                const arrayBuffer = event.target?.result as ArrayBuffer
                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
                const wavBlob = audioBufferToWav(audioBuffer)
                audioContext.close()
                resolve(wavBlob)
            } catch (error) {
                audioContext.close()
                reject(error)
            }
        }

        fileReader.onerror = () => {
            reject(new Error('Failed to read audio file'))
        }

        fileReader.readAsArrayBuffer(audioBlob)
    })
}

/**
 * Gets the appropriate MIME type for recording
 * Returns the best supported format that can be converted to WAV
 */
export const getSupportedMimeType = (): string => {
    const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/mpeg'
    ]

    for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
            return type
        }
    }

    return '' // Browser default
}
